import { Alert } from 'react-bootstrap'
import * as H from 'history'
import  React from 'react'
import { ClaimController } from '../../controllers/ClaimController'
import { ClaimModel } from '../../models/ClaimModel'
import { Consultant } from '../../models/Consultant'
import { Option } from '../../models/Option'
import { ISupportSpinner } from '../../shared/Ajax'
import { Backdrop } from '../../shared/Backdrop'
import {
  AddressField, BooleanRadioButtonField, Checkbox, EmailField, FormItem, MoneyField, NumberField, PhoneField, TextField, TextSelectField, validateFields, yesNoDataSource, NumericSelectField, NumericRadioButtonField, ErrorText,
} from '../../shared/fields'
import { MaskedField } from '../../shared/fields/MaskedField'
import {
  BusinessCategories, getFY, PrincipalAdditionalDocument, PerformanceMeasures,
} from '../../shared/Utility'
import { NextBackButtons } from './controls/NextBackButtons'

export interface PersonalisePageProps {
    history: H.History
    model: ClaimModel
    consultants: Consultant[]
    addressCountries: Option<string>[]
    onChange: { (changes: Partial<ClaimModel>): any }
    preview?: boolean
}

export interface PersonalisePageState {
    loading: boolean
    validated: boolean
}

export class PersonalisePage extends React.Component<PersonalisePageProps, PersonalisePageState> implements ISupportSpinner {
  // #region Fields

    claimRelatedCompanies: BooleanRadioButtonField
    otherBusiness: BooleanRadioButtonField
    otherBusinessName: TextField
    stakeholderOtherBusiness: BooleanRadioButtonField
    stakeholderOtherBusinessName: TextField
    grantsReceived: BooleanRadioButtonField
    grantsReceivedCount: NumberField
    emdgClientNumber: MaskedField
    claimTwoYears: BooleanRadioButtonField
    loss: BooleanRadioButtonField
    lossAmount: MoneyField
    principalAdditionalDocument: NumericSelectField
    claimFinancialAssistance: BooleanRadioButtonField

    consultantCode: TextSelectField
    consultantFirstName: TextField
    consultantLastName: TextField
    consultantBuisnessName: TextField
    consultantAddress: AddressField
    consultantPhone: PhoneField
    consultantMobile: PhoneField
    consultantEmail: EmailField

    claimOverseasRepExpenses: Checkbox
    claimMarketingConsultantExpenses: Checkbox
    claimMarketingVisitExpenses: Checkbox
    claimFreeSampleExpenses: Checkbox
    claimTradeFairsPromotionalEventsExpenses: Checkbox
    claimPromotionalLiteratureAdvertisingExpenses: Checkbox
    claimOverseasBuyersExpenses: Checkbox
    claimIpRegistrationExpenses: Checkbox
    claimIpInsuranceExpenses: Checkbox
    noExportEarningReceived: Checkbox
    claimGoodEarnings: Checkbox
    claimServiceEarnings: Checkbox
    claimTourismEarnings: Checkbox
    claimEventEarnings: Checkbox
    claimEntityEarnings: Checkbox

    expensesError: HTMLDivElement
    earningsError: HTMLDivElement

    // #endregion Fields

    ajax: ClaimController

    constructor(props: PersonalisePageProps) {
      super(props)
      this.ajax = new ClaimController()
      this.state = {
        loading: false,
        validated: false,
      }
    }

    componentWillUnmount() {
      this.ajax.cancel()
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    componentDidUpdate(prevProps: PersonalisePageProps, prevState: PersonalisePageState) {
      if (this.props.model.consultantId != prevProps.model.consultantId) {
        const match = this.props.consultants.filter((c) => c.id == this.props.model.consultantId)
        if (match.length > 0) {
          const consultant = match[0]
          this.props.onChange({
            consultantCode: consultant.code,
            consultantFirstName: consultant.firstName,
            consultantLastName: consultant.lastName,
            consultantBusinessName: consultant.businessName,
            consultantAddress: consultant.address,
            consultantPhone: consultant.phone,
            consultantMobile: consultant.mobile,
            consultantEmail: consultant.email,
            isQIPConsultant: consultant.isQIPConsultant,
          })
        }
      }

      // #region Clear dependancies

      if (this.props.model.grantsReceived != prevProps.model.grantsReceived) {
        // If "Have you received any grants" changed to "No", clear the "How many grants received" and "EMDG client number"
        if (!this.props.model.grantsReceived
                && (this.props.model.grantsReceivedCount || this.props.model.emdgClientNumber)) {
          this.props.onChange({
            grantsReceivedCount: null,
            emdgClientNumber: null,
          })
        }

        // If "Have you received any grants" changed to "Yes", clear the "Claim 2 yrs flag" and "Loss"
        if (this.props.model.grantsReceived
                && (this.props.model.claimTwoYears || this.props.model.loss)) {
          this.props.onChange({
            claimTwoYears: null,
            loss: null,
            lossAmount: null,
          })
        }
      }

      // If "Grants received count" changed
      if (this.props.model.grantsReceivedCount != prevProps.model.grantsReceivedCount) {
        // Default perfromance measure to Option A
        if (this.props.model.grantsReceivedCount >= 2
                && this.props.model.businessCategory != BusinessCategories.ApprovedBody
                && this.props.model.claimYear != 2020 // Performance measure test has been removed for the 2019-20 grant year
                && (this.props.model.performanceMeasure == undefined || this.props.model.performanceMeasure == null)) {
          this.props.onChange({
            performanceMeasure: PerformanceMeasures.OptionA,
          })
        }

        // If "grant received count" changed to < 2 clear "performance measure"
        if (this.props.model.grantsReceivedCount < 2 && this.props.model.performanceMeasure) {
          this.props.onChange({
            performanceMeasure: null,
          })
        }

        // If "grant received count" changed to > 1 clear "principal additional document"
        if (this.props.model.grantsReceivedCount > 1 && this.props.model.principalAdditionalDocument) {
          this.props.onChange({
            principalAdditionalDocument: null,
          })
        }
      }

      // If "loss flag" changed to No, clear "loss amount"
      if (this.props.model.loss != prevProps.model.loss
            && !this.props.model.loss
            && this.props.model.lossAmount) {
        this.props.onChange({
          lossAmount: null,
        })
      }

      // If "carrying on other business" changed to no, clear other business name
      if (this.props.model.otherBusiness != prevProps.model.otherBusiness
            && !this.props.model.otherBusiness
            && this.props.model.otherBusinessName) {
        this.props.onChange({
          otherBusinessName: null,
        })
      }

      // If "invoved in other business" changed to no, clear other business name
      if (this.props.model.stakeholderOtherBusiness != prevProps.model.stakeholderOtherBusiness
            && !this.props.model.stakeholderOtherBusiness
            && this.props.model.stakeholderOtherBusinessName) {
        this.props.onChange({
          stakeholderOtherBusinessName: null,
        })
      }

      // #endregion Clear dependancies
    }

    maxGrants(): number {
      switch (this.props.model.businessCategory) {
        case BusinessCategories.ApprovedJointVenture:
          return 5
        case BusinessCategories.ApprovedBody:
          return 999
        default:
          return 8
      }
    }

    areAnyExportEarningDetailsSelected = () => (this.props.model.noExportEarningReceived
            || this.props.model.claimGoodEarnings
            || this.props.model.claimEventEarnings
            || this.props.model.claimTourismEarnings
            || this.props.model.claimServiceEarnings
            || this.props.model.claimEntityEarnings)

    areExportEarningDetailsSelected = () => (this.props.model.claimGoodEarnings
            || this.props.model.claimEventEarnings
            || this.props.model.claimTourismEarnings
            || this.props.model.claimServiceEarnings
            || this.props.model.claimEntityEarnings)

    areExpenseClaimsValid(): boolean {
      return this.props.model.claimOverseasRepExpenses
            || this.props.model.claimMarketingConsultantExpenses
            || this.props.model.claimMarketingVisitExpenses
            || this.props.model.claimFreeSampleExpenses
            || this.props.model.claimTradeFairsPromotionalEventsExpenses
            || this.props.model.claimPromotionalLiteratureAdvertisingExpenses
            || this.props.model.claimOverseasBuyersExpenses
            || this.props.model.claimIpRegistrationExpenses
            || this.props.model.claimIpInsuranceExpenses
    }

    next = () => {
      this.setState({ validated: true }, () => {
        validateFields((res) => {
          if (!this.areExpenseClaimsValid()) {
            this.claimOverseasRepExpenses.focus()
            return
          }

          if (!this.areAnyExportEarningDetailsSelected()) {
            this.noExportEarningReceived.focus()
            return
          }

          if (res) {
            this.ajax.updateClaim(this, this.props.model, () => {
              this.props.history.push(`/claim/${this.props.model.claimId}/prepare`)
            })
          }
        },
        this.claimRelatedCompanies,
        this.otherBusiness,
        this.otherBusinessName,
        this.stakeholderOtherBusiness,
        this.stakeholderOtherBusinessName,
        this.grantsReceived,
        this.grantsReceivedCount,
        this.emdgClientNumber,
        this.claimTwoYears,
        this.loss,
        this.lossAmount,
        this.principalAdditionalDocument,
        this.claimFinancialAssistance,
        this.consultantCode,
        this.consultantFirstName,
        this.consultantLastName,
        this.consultantBuisnessName,
        this.consultantAddress,
        this.consultantPhone,
        this.consultantMobile,
        this.consultantEmail,
        this.claimOverseasRepExpenses,
        this.claimMarketingConsultantExpenses,
        this.claimMarketingVisitExpenses,
        this.claimFreeSampleExpenses,
        this.claimTradeFairsPromotionalEventsExpenses,
        this.claimPromotionalLiteratureAdvertisingExpenses,
        this.claimIpRegistrationExpenses,
        this.claimIpInsuranceExpenses,
        this.claimOverseasBuyersExpenses,
        this.claimGoodEarnings,
        this.claimServiceEarnings,
        this.claimTourismEarnings,
        this.claimEventEarnings,
        this.claimEntityEarnings)
      })
    }

    render() {
      const firstTimeApplicant = this.props.model.grantsReceived === false

      const principalAdditionalDocumentDataSource = [
        { value: PrincipalAdditionalDocument.InternationalSalesInvoiceOrContractInRelationToGrantYear, label: `International sales invoice or contract in relation to ${getFY(this.props.model.claimYear)} grant year` },
        { value: PrincipalAdditionalDocument.InternationalSalesInvoiceOrContractPostGrantYear, label: `International sales invoice or contract post the ${getFY(this.props.model.claimYear)} grant year` },
        { value: PrincipalAdditionalDocument.DomesticSalesInvoice, label: `Domestic sales invoice in relation to ${getFY(this.props.model.claimYear)} grant year` },
        { value: PrincipalAdditionalDocument.BankStatementShowingPayment, label: 'Bank statement showing payment' },
        { value: PrincipalAdditionalDocument.Contract, label: 'Contract' },
        { value: PrincipalAdditionalDocument.Other, label: 'Other - e.g. Evidence of IP registration or AusIndustry grant' },
      ]

      return (
        <>
          <h2 className="form-hidden-preview">Personalise claim</h2>

          <h3>Grant History</h3>
          <BooleanRadioButtonField
            ref={(r) => this.grantsReceived = r}
            id="grantsReceived"
            label="29. Have you ever received an EMDG grant?"
            value={this.props.model.grantsReceived}
            onChange={(grantsReceived) => this.props.onChange({ grantsReceived })}
            dataSource={yesNoDataSource}
            required={!this.props.model.grantsReceivedCountAsPerCMS}
            visible={!this.props.model.grantsReceivedCountAsPerCMS}
            preview={this.props.preview}
          />
          <NumberField
            ref={(r) => this.grantsReceivedCount = r}
            id="grantsReceivedCount"
            label="30. How many grants have you received? (including grant history inherited under s94 of the act)"
            value={this.props.model.grantsReceivedCount}
            onChange={(grantsReceivedCount) => this.props.onChange({ grantsReceivedCount })}
            required={this.props.model.grantsReceived}
            visible={this.props.model.grantsReceived || false}
            min={1}
            max={this.maxGrants() - 1}
            maxMessage={`${this.maxGrants()} grants are the maximum number of grants that can be claimed`}
            wholeNumbersOnly
            block={false}
            preview={this.props.preview}
          />
          <MaskedField
            ref={(r) => this.emdgClientNumber = r}
            id="emdgClientNumber"
            label="31. Provide your EMDG client number if you know it"
            value={this.props.model.emdgClientNumber}
            onChange={(emdgClientNumber) => this.props.onChange({ emdgClientNumber })}
            visible={this.props.model.grantsReceived || false}
            block={false}
            preview={this.props.preview}
            mask="9/99999"
            maskChar={null}
            errorMessage={this.props.model.emdgClientNumber && (this.props.model.emdgClientNumber.match(/\d/g) || '').length < 2 && 'Emdg client number should contain atleast 2 digits'}
          />
          <BooleanRadioButtonField
            ref={(r) => this.claimTwoYears = r}
            id="claimTwoYears"
            label="32. Are you claiming two years of expenses?"
            value={this.props.model.claimTwoYears}
            onChange={(claimTwoYears) => this.props.onChange({ claimTwoYears })}
            dataSource={yesNoDataSource}
            required={firstTimeApplicant}
            visible={firstTimeApplicant}
            preview={this.props.preview}
          />
          <div className="mt-2" style={{ display: firstTimeApplicant ? '' : 'none' }}>
            <Alert className="emdg-page-alerts">
              First time applicants can amalgamate expenses incurred in the grant year with those of the year immediately preceding the grant year to meet the eligible expense threshold of A$15,000.
            </Alert>
          </div>
          <NumericSelectField
            ref={(r) => this.principalAdditionalDocument = r}
            id="principalAdditionalDocument"
            label={'33. You have answered "Yes" to being a principal or intended principal in the export business you are promoting. Please provide one of the documents in the list below'}
            value={this.props.model.principalAdditionalDocument}
            onChange={(principalAdditionalDocument) => this.props.onChange({ principalAdditionalDocument })}
            dataSource={principalAdditionalDocumentDataSource}
            block
            required={this.props.model.grantsReceivedCount <= 1 && this.props.model.exportPrincipal}
            requiredMessage="Please select a document from the list"
            visible={this.props.model.grantsReceivedCount <= 1 && this.props.model.exportPrincipal}
            preview={this.props.preview}
          />

          <h3 style={{ display: firstTimeApplicant ? '' : 'none' }}>Loss</h3>
          <BooleanRadioButtonField
            ref={(r) => this.loss = r}
            id="loss"
            label={`34. Did you make a loss for the ${getFY(this.props.model.claimYear)} financial year (only first-time applicants)?`}
            value={this.props.model.loss}
            onChange={(loss) => this.props.onChange({ loss })}
            dataSource={yesNoDataSource}
            required={firstTimeApplicant}
            visible={firstTimeApplicant}
            preview={this.props.preview}
          />
          <MoneyField
            ref={(r) => this.lossAmount = r}
            id="lossAmount"
            label="35. Loss amount"
            value={this.props.model.lossAmount}
            onChange={(lossAmount) => this.props.onChange({ lossAmount })}
            required={!!this.props.model.loss}
            visible={!!this.props.model.loss}
            block={false}
            preview={this.props.preview}
          />

          <h3>Financial Assistance</h3>
          <BooleanRadioButtonField
            ref={(r) => this.claimFinancialAssistance = r}
            id="claimFinancialAssistance"
            label="36. Have you applied for or received a grant or reimbursement from a government department, business or third party for part or all of the claimed expenses?"
            value={this.props.model.claimFinancialAssistance}
            onChange={(claimFinancialAssistance) => this.props.onChange({ claimFinancialAssistance })}
            dataSource={yesNoDataSource}
            required
            preview={this.props.preview}
            help={(
              <>
                <h5>Grants or reimbursement received from government or third parties</h5>
                {' '}
                <p>Any reimbursement or contribution from another party that is related to the claimed promotional expense, must be declared to Austrade. This includes grants from local, State and Federal government and payments from other businesses.</p>
                {' '}
              </>
)}
          />

          <h3>Business Structure</h3>
          <div style={{ display: this.props.model.businessCategory == BusinessCategories.Company ? '' : 'none' }}>
            <BooleanRadioButtonField
              ref={(r) => this.claimRelatedCompanies = r}
              id="claimRelatedCompanies"
              label={`37. Were you part of a group of related companies at 30 June ${this.props.model.claimYear}?`}
              value={this.props.model.claimRelatedCompanies}
              onChange={(claimRelatedCompanies) => this.props.onChange({ claimRelatedCompanies })}
              dataSource={yesNoDataSource}
              required={this.props.model.businessCategory == BusinessCategories.Company}
              preview={this.props.preview}
            />
            <div className="mt-2">
              <Alert className="emdg-page-alerts">
                Related companies include all holding companies and subsidiaries, as well as any overseas registered companies.
              </Alert>
            </div>
          </div>

          <BooleanRadioButtonField
            ref={(r) => this.otherBusiness = r}
            id="otherBusiness"
            label={`38. ${this.props.model.grantsReceived ? 'Since your last EMDG application,' : 'Are you carrying on a business, or'} have you bought a business or a part of a business, or acquired significant assets from any other person/business that has or may have received an EMDG grant? Austrade may apply any previous grants paid to the new owners.`}
            value={this.props.model.otherBusiness}
            onChange={(otherBusiness) => this.props.onChange({ otherBusiness })}
            dataSource={yesNoDataSource}
            required
            preview={this.props.preview}
          />
          <TextField
            ref={(r) => this.otherBusinessName = r}
            id="otherBusinessName"
            label="39. Name of previous business"
            value={this.props.model.otherBusinessName}
            onChange={(otherBusinessName) => this.props.onChange({ otherBusinessName })}
            required={this.props.model.otherBusiness === true}
            visible={this.props.model.otherBusiness === true}
            maxLength={80}
            block={false}
            preview={this.props.preview}
          />
          <BooleanRadioButtonField
            ref={(r) => this.stakeholderOtherBusiness = r}
            id="stakeholderOtherBusiness"
            label={`40. ${this.props.model.grantsReceived ? 'Since your last EMDG application, have' : 'Have'} the directors or partners, that were recorded as office holders during ${getFY(this.props.model.claimYear)}, been involved with any other business that has previously applied for an EMDG grant? (only include businesses that have not been previously disclosed to Austrade).`}
            value={this.props.model.stakeholderOtherBusiness}
            onChange={(stakeholderOtherBusiness) => this.props.onChange({ stakeholderOtherBusiness })}
            dataSource={yesNoDataSource}
            required
            preview={this.props.preview}
          />
          <TextField
            ref={(r) => this.stakeholderOtherBusinessName = r}
            id="stakeholderOtherBusinessName"
            label="41. Name of previous business"
            value={this.props.model.stakeholderOtherBusinessName}
            onChange={(stakeholderOtherBusinessName) => this.props.onChange({ stakeholderOtherBusinessName })}
            required={this.props.model.stakeholderOtherBusiness === true}
            visible={this.props.model.stakeholderOtherBusiness === true}
            maxLength={80}
            block={false}
            preview={this.props.preview}
          />

          <h3 style={{ display: this.props.model.isConsultant ? '' : 'none' }}>Consultant details</h3>
          <p style={{ display: this.props.model.isConsultant ? '' : 'none' }}>
            42. Which Consultant prepared or assisted in the preparation of this Application?
          </p>
          <div style={{ display: this.props.model.isConsultant ? '' : 'none' }}>

            <TextSelectField
              ref={(r) => this.consultantCode = r}
              id="consultantCode"
              label="Consultant"
              value={this.props.model.consultantId}
              onChange={(consultantId) => this.props.onChange({ consultantId })}
              required={this.props.model.isConsultant}
              dataSource={this.props.consultants.map((c) => ({ value: c.id, label: c.code }))}
              preview={this.props.preview}
              block={false}
            />

            <div className="clearfix">
              <TextField
                ref={(r) => this.consultantFirstName = r}
                className="float-left mr-3"
                id="consultantFirstName"
                label="First name"
                value={this.props.model.consultantFirstName}
                onChange={(consultantFirstName) => this.props.onChange({ consultantFirstName })}
                required={this.props.model.isConsultant}
                preview={this.props.preview}
                maxLength={50}
              />
              <TextField
                ref={(r) => this.consultantLastName = r}
                className="float-left"
                id="consultantLastName"
                label="Last name"
                value={this.props.model.consultantLastName}
                onChange={(consultantLastName) => this.props.onChange({ consultantLastName })}
                required={this.props.model.isConsultant}
                preview={this.props.preview}
                maxLength={50}
              />
            </div>

            <TextField
              ref={(r) => this.consultantBuisnessName = r}
              id="consultantBuisnessName"
              label="Business name"
              value={this.props.model.consultantBusinessName}
              onChange={(consultantBusinessName) => this.props.onChange({ consultantBusinessName })}
              required={this.props.model.isConsultant}
              preview={this.props.preview}
              maxLength={80}
            />

            <AddressField
              ref={(r) => this.consultantAddress = r}
              id="consultantAddress"
              label="Postal address"
              value={this.props.model.consultantAddress}
              onChange={(consultantAddress) => this.props.onChange({ consultantAddress })}
              required={this.props.model.isConsultant}
              preview={this.props.preview}
              australianAddressesOnly
              countries={this.props.addressCountries}
            />

            <PhoneField
              ref={(r) => this.consultantPhone = r}
              id="consultantPhone"
              label="Telephone number (e.g. +61 2 9999 9999)"
              value={this.props.model.consultantPhone}
              onChange={(consultantPhone) => this.props.onChange({ consultantPhone })}
              required={this.props.model.isConsultant}
              block={false}
              preview={this.props.preview}
            />
            <PhoneField
              ref={(r) => this.consultantMobile = r}
              id="consultantMobile"
              label="Mobile phone number (e.g. +61 499 999 999)"
              value={this.props.model.consultantMobile}
              onChange={(consultantMobile) => this.props.onChange({ consultantMobile })}
              required={this.props.model.isConsultant}
              block={false}
              preview={this.props.preview}
            />
            <EmailField
              ref={(r) => this.consultantEmail = r}
              id="consultantEmail"
              label="Email"
              value={this.props.model.consultantEmail}
              onChange={(consultantEmail) => this.props.onChange({ consultantEmail })}
              required={this.props.model.isConsultant}
              preview={this.props.preview}
              maxLength={80}
            />

            <Alert className="emdg-page-alerts">
              Please Note: Austrade will use email addresses for contact person and consultant for all correspondence and may send commercial-in-confidence information
            </Alert>
          </div>

          <h3>Expense details</h3>
          <FormItem label="43. Please select expense category you are claiming.">
            <div className="mt-3 mb-3">
              <Alert className="emdg-page-alerts">
                Some expenses require supporting documents to be uploaded with the application. As you make changes to the expenses, the document(s) requested may change.
                <br />
                Do NOT include any expenses for which you have been fully funded, reimbursed, paid, or are entitled to be paid, by a third party, e.g. by another business or government program.
                <p>Where expenses are partially funded or paid by a third party, you can include the balance in your EMDG claim. Please include the full cost in the schedules and apply the eligible percentage.</p>
              </Alert>
            </div>
            <ErrorText text={this.state.validated && !this.areExpenseClaimsValid() ? 'Please select at least one checkbox' : null} />
            <Checkbox
              ref={(r) => this.claimOverseasRepExpenses = r}
              id="claimOverseasRepExpenses"
              label="Overseas representatives"
              onChange={(claimOverseasRepExpenses) => this.props.onChange({ claimOverseasRepExpenses })}
              value={this.props.model.claimOverseasRepExpenses}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimMarketingConsultantExpenses = r}
              id="claimMarketingConsultantExpenses"
              label="Marketing consultants"
              onChange={(claimMarketingConsultantExpenses) => this.props.onChange({ claimMarketingConsultantExpenses })}
              value={this.props.model.claimMarketingConsultantExpenses}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimMarketingVisitExpenses = r}
              id="claimMarketingVisitExpenses"
              label="Marketing visits"
              onChange={(claimMarketingVisitExpenses) => this.props.onChange({ claimMarketingVisitExpenses })}
              value={this.props.model.claimMarketingVisitExpenses}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimFreeSampleExpenses = r}
              id="claimFreeSampleExpenses"
              label="Free samples"
              onChange={(claimFreeSampleExpenses) => this.props.onChange({ claimFreeSampleExpenses })}
              value={this.props.model.claimFreeSampleExpenses}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimTradeFairsPromotionalEventsExpenses = r}
              id="claimTradeFairsPromotionalEventsExpenses"
              label="Trade fairs and promotions"
              onChange={(claimTradeFairsPromotionalEventsExpenses) => this.props.onChange({ claimTradeFairsPromotionalEventsExpenses })}
              value={this.props.model.claimTradeFairsPromotionalEventsExpenses}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimPromotionalLiteratureAdvertisingExpenses = r}
              id="claimPromotionalLiteratureAdvertisingExpenses"
              label="Promotional literature and advertising"
              onChange={(claimPromotionalLiteratureAdvertisingExpenses) => this.props.onChange({ claimPromotionalLiteratureAdvertisingExpenses })}
              value={this.props.model.claimPromotionalLiteratureAdvertisingExpenses}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimOverseasBuyersExpenses = r}
              id="claimOverseasBuyersExpenses"
              label="Overseas buyers"
              onChange={(claimOverseasBuyersExpenses) => this.props.onChange({ claimOverseasBuyersExpenses })}
              value={this.props.model.claimOverseasBuyersExpenses}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimIpRegistrationExpenses = r}
              id="claimIpRegistrationExpenses"
              label="Registration of eligible intellectual property (IP)"
              onChange={(claimIpRegistrationExpenses) => this.props.onChange({ claimIpRegistrationExpenses })}
              value={this.props.model.claimIpRegistrationExpenses}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimIpInsuranceExpenses = r}
              id="claimIpInsuranceExpenses"
              label="Insurance of eligible intellectual property (IP)"
              onChange={(claimIpInsuranceExpenses) => this.props.onChange({ claimIpInsuranceExpenses })}
              value={this.props.model.claimIpInsuranceExpenses}
              preview={this.props.preview}
            />
          </FormItem>

          <h3>Export earning details</h3>
          <FormItem label="44. Please select export earnings category relative to your business. This section is mandatory for all applicants.">
            <div className="mt-3 mb-3">
              <Alert className="emdg-page-alerts">
                If you have more than 50 export earnings transactions this grant year, you can:
                <ul>
                  <li>provide details of individual transactions for 1 month (ie enter the date in the 'Date earnings received. Actual date' column), and</li>
                  <li>provide monthly totals for other months (ie use 'Date earnings received. Month only' column).</li>
                </ul>
              </Alert>
            </div>
            <ErrorText text={this.state.validated && !this.areAnyExportEarningDetailsSelected() ? 'Please select at least one checkbox' : null} />
            <Checkbox
              ref={(r) => this.noExportEarningReceived = r}
              id="noExportEarningReceived"
              label="No export earnings received in grant year"
              value={this.props.model.noExportEarningReceived}
              onChange={(noExportEarningReceived) => this.props.onChange({ noExportEarningReceived })}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimGoodEarnings = r}
              id="claimGoodEarnings"
              label="Goods"
              onChange={(claimGoodEarnings) => this.props.onChange({ claimGoodEarnings })}
              value={this.props.model.noExportEarningReceived ? this.props.model.claimGoodEarnings = false : this.props.model.claimGoodEarnings}
              disabled={this.props.model.noExportEarningReceived}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimServiceEarnings = r}
              id="claimServiceEarnings"
              label="Services, intellectual property, or know-how"
              onChange={(claimServiceEarnings) => this.props.onChange({ claimServiceEarnings })}
              value={this.props.model.noExportEarningReceived ? this.props.model.claimServiceEarnings = false : this.props.model.claimServiceEarnings}
              disabled={this.props.model.noExportEarningReceived}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimTourismEarnings = r}
              id="claimTourismEarnings"
              label="Tourism services"
              onChange={(claimTourismEarnings) => this.props.onChange({ claimTourismEarnings })}
              value={this.props.model.noExportEarningReceived ? this.props.model.claimTourismEarnings = false : this.props.model.claimTourismEarnings}
              disabled={this.props.model.noExportEarningReceived}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimEventEarnings = r}
              id="claimEventEarnings"
              label="Events promoters"
              onChange={(claimEventEarnings) => this.props.onChange({ claimEventEarnings })}
              value={this.props.model.noExportEarningReceived ? this.props.model.claimEventEarnings = false : this.props.model.claimEventEarnings}
              disabled={this.props.model.noExportEarningReceived}
              preview={this.props.preview}
            />
            <Checkbox
              ref={(r) => this.claimEntityEarnings = r}
              id="claimEntityEarnings"
              label="Related entities"
              onChange={(claimEntityEarnings) => this.props.onChange({ claimEntityEarnings })}
              value={this.props.model.noExportEarningReceived ? this.props.model.claimEntityEarnings = false : this.props.model.claimEntityEarnings}
              disabled={this.props.model.noExportEarningReceived}
              preview={this.props.preview}
            />
          </FormItem>

          <NextBackButtons
            onBack={() => this.props.history.push(`/claim/${this.props.model.claimId}/business`)}
            onNext={this.next}
            preview={this.props.preview}
          />

          <Backdrop on={this.state.loading} />
        </>
      )
    }
}
